/**
 * Scenarios — the merged SKU-velocity-field + capacity-scenario screen (build handover §1–§9).
 *
 * Three columns: the right rail's five levers are the causes, the centre is the live diagnosis,
 * the left rail's four views are the saved-case consequences. Edits only happen on the right;
 * the centre reacts on every edit; the left rail holds until a scenario is saved.
 *
 * All arithmetic comes from `@/lib/data/scenario.ts` (`runScenario`, `buildClaimMap`, …) — this
 * file only holds the interactive state (`state.ts`'s reducer) and the layout.
 */

import "./scenarios.css";

import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import {
  type CapacityModel,
  engaged as scenarioEngaged,
  type LeverFamily,
  neutralScenarioParams,
  runScenario,
  sameParams,
  type ScenarioContext,
  type Site,
  type Sku,
  type SpaceLeverId,
  useCapacity,
  useLeverFamilies,
  useSite,
  useSkus,
} from "@/lib/data";
import { cn } from "@/lib/utils";

import { useNavRail } from "../shell";
import { ScenarioAgent } from "./agent";
import { AgentRead } from "./agent-read";
import { CapacityTable } from "./capacity-table";
import { LeverDrawer } from "./drawer";
import { FourViewModal } from "./four-view-modal";
import { FourViews, selectedResult } from "./four-views";
import { LeverCards } from "./levers";
import { assignPlacements, SYSTEM_ADD_FACES_FRACTION } from "./mock";
import { PickFaces } from "./pick-faces";
import { SpotlightOverlay, type SpotlightTarget } from "./spotlight";
import { initScenariosState, paramsToWire, scenariosReducer } from "./state";
import { VelocityScatter } from "./velocity-scatter";

interface ScenariosReadyProps {
  skus: Sku[];
  site: Site;
  model: CapacityModel;
  families: Record<SpaceLeverId, LeverFamily[]>;
}

function ScenariosBody({ skus, site, model, families }: ScenariosReadyProps) {
  const pinchYear = model.years?.[1] ?? 2027;
  const [state, dispatch] = useReducer(scenariosReducer, pinchYear, initScenariosState);
  // The spotlight ring is measured against `.body` (which spans all three columns), so it can
  // ring a lever card in the right rail as the agent moves it — the levers stay mounted now that
  // the chat is a drawer over the centre rather than a replacement for the lever stack.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [spotlight, setSpotlight] = useState<SpotlightTarget | null>(null);

  // A first question handed in via router state (e.g. the welcome dialog's "See it in action"):
  // open the chat and let the agent auto-ask it once. This keys on `location.key` (a fresh id per
  // navigation) rather than mount, because the welcome dialog lives in the shell that already
  // wraps this route — so clicking through arrives here without remounting, and a mount-only read
  // would miss the state. The seed is then cleared from history so a refresh doesn't re-fire it,
  // and `initialAsk` is nulled once the agent consumes it so reopening the chat later starts clean.
  const location = useLocation();
  const navigate = useNavigate();
  const [initialAsk, setInitialAsk] = useState<string | null>(null);
  useEffect(() => {
    const ask = (location.state as { ask?: string } | null)?.ask;
    if (ask) {
      setInitialAsk(ask);
      dispatch({ type: "chat", on: true });
      // Clear the seed through React Router (not raw `history.replaceState`, which would wipe
      // RR's own `{ key, idx }` payload and break back/forward tracking) — same entry, state
      // dropped, so a refresh won't replay it.
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: null },
      );
    }
  }, [location.key]);

  // Opening the chat collapses the outer nav rail to reclaim width for the chat column, then
  // restores whatever the rail was before once the chat closes — or on unmount, so navigating
  // away with the chat still open never leaves the rail stuck collapsed. `navRail` is read
  // through a ref so the effect keys only on `chatOn`: `navRail`'s identity changes every time
  // the collapse state flips, which would otherwise re-run this mid-interaction.
  const navRail = useNavRail();
  const navRailRef = useRef(navRail);
  navRailRef.current = navRail;
  const priorCollapsed = useRef<boolean | null>(null);
  useEffect(() => {
    const nav = navRailRef.current;
    if (!nav) {
      return;
    }
    if (state.chatOn) {
      if (priorCollapsed.current === null) {
        priorCollapsed.current = nav.collapsed;
      }
      nav.setCollapsed(true);
    } else if (priorCollapsed.current !== null) {
      nav.setCollapsed(priorCollapsed.current);
      priorCollapsed.current = null;
    }
  }, [state.chatOn]);
  useEffect(
    () => () => {
      const nav = navRailRef.current;
      if (nav && priorCollapsed.current !== null) {
        nav.setCollapsed(priorCollapsed.current);
        priorCollapsed.current = null;
      }
    },
    [],
  );

  const ctx: ScenarioContext = useMemo(
    () => ({ skus, site, model, families, systemAddFacesFraction: SYSTEM_ADD_FACES_FRACTION }),
    [skus, site, model, families],
  );
  const placements = useMemo(
    () => assignPlacements(skus, model.capd.ceil),
    [skus, model.capd.ceil],
  );
  const result = useMemo(() => runScenario(state.draft, ctx), [state.draft, ctx]);
  const restResult = useMemo(
    () => runScenario(neutralScenarioParams(pinchYear), ctx),
    [ctx, pinchYear],
  );
  const sm = useMemo(
    () => selectedResult(state.scenarios, state.viewSel, ctx),
    [state.scenarios, state.viewSel, ctx],
  );

  const selectedScn = state.viewSel >= 0 ? state.scenarios[state.viewSel] : undefined;
  const saveEnabled =
    scenarioEngaged(state.draft) && (!selectedScn || !sameParams(state.draft, selectedScn.params));

  return (
    <div className="scn">
      <div className="body" ref={bodyRef}>
        {/* One ring for all three columns — measured against `.body` so it can frame a lever
            card in the right rail (or a centre section) wherever the agent is working. */}
        <SpotlightOverlay containerRef={bodyRef} target={spotlight} />

        <FourViews
          scenarios={state.scenarios}
          viewSel={state.viewSel}
          onSelect={(index) => dispatch({ type: "selectScenario", index })}
          ctx={ctx}
          restResult={restResult}
          onOpen={(vmodal) => dispatch({ type: "vmodal", vmodal })}
        />

        <div className="centre">
          <CapacityTable draft={state.draft} result={result} ctx={ctx} dispatch={dispatch} />
          <PickFaces
            skus={skus}
            placements={placements}
            claim={result.claim}
            draft={state.draft}
            ctx={ctx}
            filter={state.filter}
            dispatch={dispatch}
          />
          <VelocityScatter
            skus={skus}
            placements={placements}
            draft={state.draft}
            result={result}
            filter={state.filter}
            dispatch={dispatch}
          />
        </div>

        {/* Levers stay mounted whether or not the chat is open — the chat is a drawer over the
            centre (below), so you watch the agent move these and the spotlight rings them. */}
        <div className="rail-right">
          <div className="rail-body">
            <LeverCards
              draft={state.draft}
              result={result}
              ctx={ctx}
              dispatch={dispatch}
              onDrawer={(drawer) => dispatch({ type: "drawer", drawer })}
              onHot={(hot) => dispatch({ type: "hot", hot })}
            />
            <AgentRead
              draft={state.draft}
              result={result}
              ctx={ctx}
              resolveNote={state.resolveNote}
              resolveTone={state.resolveTone}
              onDismiss={() => dispatch({ type: "dismissNote" })}
            />
            <div className="rr-foot">
              <button
                className={cn("rr-btn agent", state.chatOn && "active")}
                onClick={() => dispatch({ type: "chat", on: true })}
              >
                Ask Agent
              </button>
              <button className="rr-btn agent" onClick={() => dispatch({ type: "resolve", ctx })}>
                Agent Resolve
              </button>
              <button
                className="rr-btn"
                onClick={() => dispatch({ type: "reset", year: pinchYear })}
              >
                Reset
              </button>
              <button
                className="rr-btn primary"
                disabled={!saveEnabled}
                onClick={() => dispatch({ type: "save" })}
              >
                Save Scenario
              </button>
            </div>
          </div>
        </div>

        {/* Ask-Agent chat: a vertical pane to the RIGHT of the levers. The outer nav rail
            collapses (see the effect above) to make room; the levers stay visible so you watch
            the agent move them and the spotlight ring finds them. */}
        {state.chatOn ? (
          <div className="agent-pane">
            <ScenarioAgent
              scenario={paramsToWire(state.draft)}
              dispatch={dispatch}
              onSpotlight={setSpotlight}
              initialAsk={initialAsk}
              onInitialAskConsumed={() => setInitialAsk(null)}
              onClose={() => {
                dispatch({ type: "chat", on: false });
                setSpotlight(null);
              }}
            />
          </div>
        ) : null}
      </div>

      <LeverDrawer
        lever={state.drawer}
        draft={state.draft}
        result={result}
        ctx={ctx}
        onClose={() => dispatch({ type: "drawer", drawer: null })}
      />
      <FourViewModal
        vmodal={state.vmodal}
        scenarios={state.scenarios}
        viewSel={state.viewSel}
        sm={sm}
        ctx={ctx}
        onClose={() => dispatch({ type: "vmodal", vmodal: null })}
      />
    </div>
  );
}

export function ScenariosScreen() {
  const skusQ = useSkus();
  const siteQ = useSite();
  const capacityQ = useCapacity();
  const reloQ = useLeverFamilies("relo");
  const packQ = useLeverFamilies("pack");
  const convQ = useLeverFamilies("conv");

  const ready: ScenariosReadyProps | null =
    skusQ.data && siteQ.data && capacityQ.data && reloQ.data && packQ.data && convQ.data
      ? {
          skus: skusQ.data,
          site: siteQ.data,
          model: capacityQ.data,
          families: { relo: reloQ.data, pack: packQ.data, conv: convQ.data },
        }
      : null;

  if (!ready) {
    return (
      <div className="scn">
        <div className="scn-loading">Loading the site&apos;s SKU population…</div>
      </div>
    );
  }
  return <ScenariosBody {...ready} />;
}
